import { useEffect, useState, useCallback } from "react";
import api, { downloadFile } from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { DownloadSimple, FunnelSimple, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2", "#db2777"];

const Kpi = ({ label, value, suffix, testid }) => (
  <Card data-testid={testid} className="flex flex-col gap-2 rounded-md border p-5">
    <Label2>{label}</Label2>
    <div className="font-heading text-3xl font-black tracking-tighter">
      {value}<span className="ml-0.5 text-base text-muted-foreground">{suffix}</span>
    </div>
  </Card>
);

const ChartCard = ({ title, children, testid }) => (
  <Card data-testid={testid} className="rounded-md border p-5">
    <h3 className="mb-4 font-heading text-sm font-bold tracking-tight">{title}</h3>
    <div className="h-64">{children}</div>
  </Card>
);

export default function Analytics() {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [drill, setDrill] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    api.get(`/analytics/query?${p.toString()}`).then((r) => setData(r.data));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const openDrill = async (metric, value) => {
    const p = new URLSearchParams({ metric });
    if (value) p.set("value", value);
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    const { data: d } = await api.get(`/analytics/drilldown?${p.toString()}`);
    setDrill(d);
  };

  const exportCsv = async () => {
    const p = new URLSearchParams();
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    try {
      await downloadFile(`/analytics/export?${p.toString()}`, "csp-analytics.csv");
      toast.success("CSV-Export erstellt");
    } catch { toast.error("Export fehlgeschlagen"); }
  };

  if (!data) return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-md" />)}
    </div>
  );

  const k = data.kpis;
  return (
    <div>
      <PageHeader title="Analytics" subtitle="Warum & Trends? · Historische Auswertungen, KPI-Berichte (Read-Model / CQRS)">
        <Button data-testid="analytics-export-btn" variant="outline" size="sm" className="gap-2" onClick={exportCsv}>
          <DownloadSimple size={16} weight="bold" /> CSV-Export
        </Button>
      </PageHeader>

      <Card className="mb-6 flex flex-wrap items-end gap-3 rounded-md border p-4">
        <div className="flex items-center gap-2 text-muted-foreground"><FunnelSimple size={18} weight="bold" /><Label2>Zeitraum</Label2></div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Von</label>
          <Input data-testid="analytics-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Bis</label>
          <Input data-testid="analytics-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" />
        </div>
        {(from || to) && (
          <Button variant="ghost" size="sm" onClick={() => { setFrom(""); setTo(""); }}>Zurücksetzen</Button>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi testid="an-ftf" label="First-Time-Fix" value={k.first_time_fix} suffix="%" />
        <Kpi testid="an-arbeit" label="Ø Arbeitszeit" value={k.avg_arbeitszeit_min} suffix="min" />
        <Kpi testid="an-sla" label="Gelöste Störungen" value={k.geloeste_stoerungen} />
        <Kpi testid="an-garantie" label="Garantiequote" value={k.garantiequote} suffix="%" />
        <Kpi testid="an-anlagen" label="Anlagen" value={k.anlagen} />
        <Kpi testid="an-reparaturen" label="Reparaturen" value={k.reparaturen} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Abgeschlossene Wartungen je Monat" testid="chart-monthly">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.monthly_maintenance}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="completed" stroke={COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Wartungsergebnisse (Klick = Drill-Down)" testid="chart-results">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.maintenance_results} onClick={(e) => e?.activeLabel && openDrill("maintenance", e.activeLabel)}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} cursor="pointer">
                {data.maintenance_results.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Störungsprioritäten" testid="chart-prio">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data.incident_priority} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={80}
                label={(e) => e.label} onClick={(e) => e?.label && openDrill("incidents", e.label)}>
                {data.incident_priority.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} cursor="pointer" />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Anlagen-Kritikalität" testid="chart-crit">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data.criticality} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={45} outerRadius={80} label={(e) => e.label}>
                {data.criticality.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent data-testid="drilldown-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ChartBar size={20} weight="duotone" /> Drill-Down: {drill?.metric} · {drill?.value}</DialogTitle>
            <DialogDescription>{drill?.count} Datensätze auf Auftragsebene</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 divide-y divide-border overflow-y-auto rounded-md border">
            {(drill?.rows || []).map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                <span className="truncate">{r.title || r.asset_name || r.description || r.id}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{r.status}</span>
              </div>
            ))}
            {drill?.rows?.length === 0 && <div className="p-6 text-sm text-muted-foreground">Keine Datensätze.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
